import {
  PlayExperience,
  playExperiences,
  MATCHMAKING_ENABLED,
} from 'src/pages/dashboard/playExperiences'
import React from 'react'
import Skeleton from '@mui/material/Skeleton'
import ViewNavigation from '@components/common/ViewNavigation'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import TodayIcon from '@mui/icons-material/Today'
import LanguageIcon from '@mui/icons-material/Language'
import CasinoIcon from '@mui/icons-material/Casino'
// import SearchIcon from '@mui/icons-material/Search'
import LiveHelpRoundedIcon from '@mui/icons-material/LiveHelpRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import DrawRoundedIcon from '@mui/icons-material/DrawRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { GlassCard, GlassOutlineButton } from '@components/common/Glass'
import DashboardTriviaTabView from './DashboardTriviaTabView'
import DashboardPollsTabView from './DashboardPollsTabView'
import DashboardCreateTabView from './DashboardCreateTabView'
import DashboardOnlineLobbyView from './DashboardOnlineLobbyView'
import { mainColorMediumText, shadow1 } from 'src/ui/theme/tokens'
import { GameSounds } from 'src/ui/sound/types'
import { UserSoloStats } from 'src/api/types/users'
import { Poll } from 'src/api/types/polls'
import { Item } from 'src/components/common/ItemGuessBox'
import { List, ListItem } from 'src/api/types/lists'
import { AlertInput } from '@components/common/Alert'
import { MatchmakingStats } from 'src/api/types/matchmaking'
import { sortIndex, toSoloItems } from 'src/pages/dashboard/listUtils'
import type { Player } from 'src/pages/Dashboard'
import Typography from '@mui/material/Typography'
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo'
import PhonelinkRoundedIcon from '@mui/icons-material/PhonelinkRounded'
import { AnimatePresence, motion } from 'framer-motion'
import Chip from '@mui/material/Chip'

export type DashboardPlayTabViewProps = {
  experience?: PlayExperience
  selectedListTab: string
  onTabChange: (value: string) => void
  sounds: GameSounds
  fullScreen: boolean
  dailyList?: List
  soloStats?: UserSoloStats
  mode: 'single' | 'multi' | 'host' | undefined
  multiMode: string
  onSelectModeClick: () => void
  onOpenHowToPlay: () => void
  setSelectedTab: (tab: string) => void
  setSelectedList: (value: number | undefined) => void
  setActivePollId: (value: number | undefined) => void
  setTopic: (value: string) => void
  setListItems: (items: Item[]) => void
  onStartGame: (listId?: number) => void
  onStartRandomList: () => void
  onStartRandomPoll: () => void
  onStartDailyPoll: () => void
  onStartPreviousPoll: () => void
  userId?: number
  pollRefreshKey: number
  onPollSelect: (poll: Poll) => void
  onPollVote: (poll: Poll, hasVoted: boolean) => void
  onPollPlay: (poll: Poll) => void
  canCreatePoll: boolean
  onStartCreatedList: (payload: { topic: string; items: Item[]; listId?: number }) => void
  onAlert: (value: AlertInput) => void
  appUserId?: number
  onPollCreated: (
    pollId: number,
    pollFormat?: 'ranked_options' | 'community_answers'
  ) => void
  onSetSelectedListTab: (tab: string) => void
  onEditList?: (list: List) => void
  listSearchShowMine?: boolean
  onListSearchShowMine?: (value: boolean) => void
  onlineUsersAll: number
  onlineUsersLogged: number
  searchingRanked: number
  searchingUnranked: number
  matchmakingRated: boolean
  onSetMatchmakingRated: (value: boolean) => void
  joinGameCode: string
  leaderboard: MatchmakingStats[]
  myStats?: MatchmakingStats
  onlineMatchRated?: boolean
  onlineMatchType?: 'matchmaking' | 'lobby'
  pendingMatch: boolean
  handleMakeMatch: () => void
  handleCreateLobby: (profile?: { name?: string; icon?: string }) => void
  handleJoinLobby: (profile?: { name?: string; icon?: string }) => void
  onChangeJoinCode: (value: string) => void
  onlineProfileName: string
  onlineProfileIcon: string
  canEditOnlineName: boolean
  onChangeOnlineName: (name: string) => void
  players: Player[]
  hostId: string
  connectionId: string
  onChangeOnlineTeam: (team: number) => void
  onChangeOnlineIcon: (icon: string) => void
  onCommitOnlineName: () => void
  onStartOnlineLobbyGame: () => void
  onCloseLobby: () => void
  onLeaveLobby: () => void
  gameCode?: string
  ws?: WebSocket
}

const DashboardPlayTabView: React.FC<DashboardPlayTabViewProps> = ({
  experience,
  selectedListTab,
  onTabChange,
  sounds,
  fullScreen,
  dailyList,
  soloStats,
  mode,
  multiMode,
  onSelectModeClick,
  onOpenHowToPlay,
  setSelectedTab,
  setSelectedList,
  setActivePollId,
  setTopic,
  setListItems,
  onStartGame,
  onStartRandomList,
  onStartRandomPoll,
  onStartDailyPoll,
  onStartPreviousPoll,
  userId,
  pollRefreshKey,
  onPollSelect,
  onPollVote,
  onPollPlay,
  canCreatePoll,
  onStartCreatedList,
  onAlert,
  appUserId,
  onPollCreated,
  onSetSelectedListTab,
  onEditList,
  listSearchShowMine,
  onListSearchShowMine,
  onlineUsersAll,
  onlineUsersLogged,
  searchingRanked,
  searchingUnranked,
  matchmakingRated,
  onSetMatchmakingRated,
  joinGameCode,
  leaderboard,
  myStats,
  onlineMatchRated,
  onlineMatchType,
  pendingMatch,
  handleMakeMatch,
  handleCreateLobby,
  handleJoinLobby,
  onChangeJoinCode,
  onlineProfileName,
  onlineProfileIcon,
  canEditOnlineName,
  onChangeOnlineName,
  players,
  hostId,
  connectionId,
  onChangeOnlineTeam,
  onChangeOnlineIcon,
  onCommitOnlineName,
  onStartOnlineLobbyGame,
  onCloseLobby,
  onLeaveLobby,
  gameCode,
  ws,
}) => {
  const showOnlineTab = experience === 'lobby' || experience === 'online'
  const showDailyTab = mode === 'single'
  const primaryTabValue = showOnlineTab ? 'online' : 'daily'
  const primaryTabLabel = showOnlineTab
    ? experience === 'lobby'
      ? 'Lobby'
      : 'Online Match'
    : 'Daily'
  const soloStatsForTabs = mode === 'single' ? soloStats : undefined
  const onlineLobbyPlayerCount =
    showOnlineTab && gameCode && onlineMatchType === 'lobby' ? players.length : 0
  const showSearchingDot =
    showOnlineTab &&
    pendingMatch &&
    (!onlineMatchType || onlineMatchType === 'matchmaking')
  const lockTabsToOnline =
    mode === 'multi' &&
    multiMode === 'online' &&
    !!gameCode &&
    !!hostId &&
    !!connectionId &&
    hostId !== connectionId
  const primaryTabIcon = showOnlineTab ? (
    <LanguageIcon fontSize="small" />
  ) : (
    <TodayIcon fontSize="small" />
  )

  const handleSelectList = (
    id: number | undefined,
    prompt?: string,
    items?: ListItem[]
  ) => {
    const newList =
      items?.map((item, i) => ({
        title: item.title,
        metric: item.metric,
        index: item.index || item.rank || i + 1,
        points: 1,
        listId: item.listId ?? 0,
      })) || []

    setSelectedList(id)
    setActivePollId(undefined)
    setTopic(prompt || '')

    const nextItems =
      mode === 'single'
        ? toSoloItems(newList, 10)
        : mode === 'host'
          ? sortIndex(newList)
          : newList
    setListItems(nextItems)
    setSelectedTab('play')

    if (mode === 'multi' && multiMode !== 'local' && gameCode) {
      ws?.send(
        JSON.stringify({
          event: 'editGame',
          data: {
            code: gameCode,
            prompt,
            list: newList,
          },
        })
      )
    }
  }

  const isTriviaContentTab =
    selectedListTab === 'daily' ||
    selectedListTab === 'random' ||
    selectedListTab === 'find'

  const tabContentKey = `${experience || 'none'}-${selectedListTab}`
  const modeLabel = experience ? playExperiences[experience].title : 'Solo Play'
  return (
    <Grid container justifyContent="center" columnSpacing={2} rowSpacing={1.25}>
      <Grid item xs={12}>
        <ViewNavigation mb={0}>
          <GlassOutlineButton
            variant="outlined"
            onClick={onSelectModeClick}
            sx={{
              pl: 2,
              pr: 3,
              py: 0.75,
              color: '#60768b',
              fontWeight: 600,
              minWidth: '140px',
              whiteSpace: 'nowrap',
            }}
            startIcon={
              <ArrowBackRoundedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />
            }
          >
            {modeLabel}
          </GlassOutlineButton>
          <GlassOutlineButton
            variant="outlined"
            onClick={onOpenHowToPlay}
            sx={{
              px: 3,
              py: 0.75,
              color: '#60768b',
              fontWeight: 600,
              minWidth: '140px',
              whiteSpace: 'nowrap',
            }}
            startIcon={<InfoOutlinedIcon fontSize="inherit" sx={{ color: '#7c91a3' }} />}
          >
            How to Play
          </GlassOutlineButton>
        </ViewNavigation>
      </Grid>
      <Grid item xs={12}>
        <GlassCard
          sx={{
            boxShadow: shadow1,
            borderRadius: '28px',
            overflow: 'hidden',
            background: '#f7fbfe',
            borderColor: '#d6e4ef',
          }}
        >
          <Tabs
            value={selectedListTab}
            onChange={(e: unknown, v: string) => {
              sounds.clickAlt()
              onTabChange(v)
              window.requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              })
            }}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              mx: { xs: 1, sm: 2 },
              mt: 1,
              mb: 1,
              overflowX: 'auto',
              '& .MuiTab-root': {
                flex: 1,
                minWidth: 58,
                minHeight: 50,
                px: { xs: 0.5, sm: 1.5 },
                fontSize: { xs: 12, sm: 14 },
                fontWeight: 700,
              },
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: '#193b58',
                color: '#ffffff',
              },
            }}
            TabIndicatorProps={{
              style: {
                display: 'none',
              },
            }}
          >
            {showOnlineTab || showDailyTab ? (
              <Tab
                value={primaryTabValue}
                icon={primaryTabIcon}
                iconPosition="top"
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {primaryTabLabel}
                    {onlineLobbyPlayerCount > 0 ? (
                      <Box
                        component="span"
                        aria-label={`${onlineLobbyPlayerCount} players in lobby`}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 19,
                          height: 19,
                          px: 0.5,
                          ml: 0.25,
                          borderRadius: '999px',
                          bgcolor: '#53d9f5',
                          color: '#193b58',
                          fontSize: 11,
                          fontWeight: 800,
                          lineHeight: 1,
                          verticalAlign: 'middle',
                        }}
                      >
                        {onlineLobbyPlayerCount}
                      </Box>
                    ) : showSearchingDot ? (
                      <Box
                        component="span"
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: '#cc4c58',
                        }}
                      />
                    ) : null}
                  </Box>
                }
              />
            ) : null}

            {experience !== 'online' && (
              <Tab
                label="Random"
                icon={<CasinoIcon fontSize="small" />}
                iconPosition="top"
                value="random"
                disabled={lockTabsToOnline}
              />
            )}
            {experience !== 'online' && (
              <Tab
                label="Trivia"
                icon={<LiveHelpRoundedIcon fontSize="small" />}
                iconPosition="top"
                value="find"
                disabled={lockTabsToOnline}
              />
            )}
            {experience !== 'online' && (
              <Tab
                label="Polls"
                icon={<HowToVoteRoundedIcon fontSize="small" />}
                iconPosition="top"
                value="polls"
                disabled={lockTabsToOnline}
              />
            )}
            {experience !== 'online' && (
              <Tab
                label="Create"
                icon={<DrawRoundedIcon fontSize="small" />}
                iconPosition="top"
                value="generate"
                disabled={lockTabsToOnline}
              />
            )}
          </Tabs>

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={tabContentKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
            >
              {mode === 'multi' && selectedListTab !== 'online' ? (
                <Box
                  mt={1}
                  px={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Chip
                    size="small"
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {gameCode ? (
                          <PhonelinkRoundedIcon
                            fontSize="inherit"
                            sx={{ fontSize: '17px' }}
                          />
                        ) : (
                          <PersonalVideoIcon
                            fontSize="inherit"
                            sx={{ fontSize: '17px' }}
                          />
                        )}
                        <Typography variant="body1" fontWeight={700}>
                          {experience === 'lobby' ? 'Separate devices' : 'Same device'}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      width: { xs: '160px', sm: '170px' },
                      minWidth: '160px',
                      background: 'rgba(255,255,255,0.9)',
                      border: '1px solid rgba(148,163,184,0.4)',
                      fontWeight: 700,
                      color: mainColorMediumText,
                      '& .MuiChip-label, & .MuiTypography-root': {
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              ) : null}

              {isTriviaContentTab ? (
                <DashboardTriviaTabView
                  selectedListTab={selectedListTab}
                  dailyList={dailyList}
                  soloStats={soloStatsForTabs}
                  fullScreen={fullScreen}
                  mode={mode}
                  multiMode={multiMode}
                  onStartGame={onStartGame}
                  onStartRandomList={onStartRandomList}
                  onStartRandomPoll={onStartRandomPoll}
                  onStartDailyPoll={onStartDailyPoll}
                  onStartPreviousPoll={onStartPreviousPoll}
                  onNavigateTab={onTabChange}
                  onSelectList={handleSelectList}
                  userId={userId}
                  onEditList={onEditList}
                  showMine={listSearchShowMine}
                  onShowMine={onListSearchShowMine}
                  sounds={sounds}
                />
              ) : null}

              {experience === 'online' && !MATCHMAKING_ENABLED ? (
                <Box role="status" sx={{ p: 3 }}>
                  <Typography fontWeight={700}>Restoring your match…</Typography>
                  {Array.from({ length: 4 }, (_, index) => (
                    <Skeleton key={index} variant="rounded" height={40} sx={{ mt: 1 }} />
                  ))}
                </Box>
              ) : mode === 'multi' && selectedListTab === 'online' ? (
                <DashboardOnlineLobbyView
                  experience={experience === 'lobby' ? 'lobby' : 'online'}
                  onlineUsersAll={onlineUsersAll}
                  onlineUsersLogged={onlineUsersLogged}
                  searchingRanked={searchingRanked}
                  searchingUnranked={searchingUnranked}
                  matchmakingRated={matchmakingRated}
                  onSetMatchmakingRated={onSetMatchmakingRated}
                  joinGameCode={joinGameCode}
                  onChangeJoinCode={onChangeJoinCode}
                  onlineProfileName={onlineProfileName}
                  onlineProfileIcon={onlineProfileIcon}
                  isAuthenticated={!!userId}
                  canEditOnlineName={canEditOnlineName}
                  onChangeOnlineName={onChangeOnlineName}
                  pendingMatch={pendingMatch}
                  gameCode={gameCode || ''}
                  leaderboard={leaderboard}
                  myStats={myStats}
                  onlineMatchRated={onlineMatchRated}
                  onlineMatchType={onlineMatchType}
                  players={players}
                  hostId={hostId}
                  connectionId={connectionId}
                  onChangeOnlineTeam={onChangeOnlineTeam}
                  onChangeOnlineIcon={onChangeOnlineIcon}
                  onCommitOnlineName={onCommitOnlineName}
                  onStartOnlineLobbyGame={onStartOnlineLobbyGame}
                  onCloseLobby={onCloseLobby}
                  onLeaveLobby={onLeaveLobby}
                  onUiAction={sounds.click}
                  handleMakeMatch={handleMakeMatch}
                  handleCreateLobby={handleCreateLobby}
                  handleJoinLobby={handleJoinLobby}
                />
              ) : null}

              {selectedListTab === 'polls' ? (
                <DashboardPollsTabView
                  selectedListTab={selectedListTab}
                  userId={userId}
                  pollRefreshKey={pollRefreshKey}
                  onPollSelect={onPollSelect}
                  onPollVote={onPollVote}
                  onPollPlay={onPollPlay}
                  sounds={sounds}
                />
              ) : null}

              {selectedListTab === 'generate' ? (
                <DashboardCreateTabView
                  selectedListTab={selectedListTab}
                  fullScreen={fullScreen}
                  multiMode={multiMode}
                  gameCode={gameCode}
                  hostId={hostId}
                  connectionId={connectionId}
                  canCreatePoll={canCreatePoll}
                  mode={mode}
                  userId={appUserId}
                  onPollCreated={onPollCreated}
                  onSetSelectedListTab={onSetSelectedListTab}
                  onStartCreatedList={onStartCreatedList}
                  onAlert={onAlert}
                  onUiAction={sounds.click}
                  onEditList={onEditList}
                  sounds={sounds}
                />
              ) : null}
            </motion.div>
          </AnimatePresence>
        </GlassCard>
      </Grid>
    </Grid>
  )
}

export default DashboardPlayTabView
